import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import { COLLECTION_ROUTE, HOME_ROUTE, MY_RECO_ROUTE, PROFILE_ROUTE } from '@/constants/routes';
import { useTranslation } from '@/utils/i18n';
import { cn } from '@/lib/utils';
import {
  AccountCircleFillIcon,
  CardsStarFillIcon,
  HeadphonesFillIcon,
  RocketFillIcon,
} from '@/components/icons/TabIcons';
import RecoFab from './RecoFab';

/**
 * ios-tab-bar — the iOS tab bar: a capsule floating over the content, after
 * Apple Music on iOS 26 in Racore's tokens. One static blur on a small surface
 * (no animation, no refraction imitation); without backdrop-filter support the
 * fill turns near-opaque. Each tab is ≥ 44 pt; the selected one is white on a
 * soft pill, the others grey; icons filled (Apple-like). The centre 🎵 is today's, centred in the capsule.
 * The screens keep `--tab-bar-inset` free under their content (see `_tabs.tsx`).
 */

interface TabLinkProps {
  to: string;
  label: string;
  icon: ReactNode;
  badge?: boolean;
  onClick?: () => void;
}

function TabLink({ to, label, icon, badge, onClick }: TabLinkProps) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="tw:flex tw:min-h-11 tw:min-w-11 tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:gap-0.5 tw:rounded-full tw:text-grey-3 tw:transition-colors tw:duration-150"
      activeProps={{ className: 'tw:bg-white/10 tw:text-white' }}
      data-testid={`tab-${to.replace('/', '')}`}
    >
      <span className="tw:relative tw:flex tw:items-center">
        {icon}
        {badge && (
          <span
            data-testid={`tab-${to.replace('/', '')}-badge`}
            className="tw:absolute tw:-right-1 tw:-top-0.5 tw:size-2 tw:rounded-full tw:bg-error"
          />
        )}
      </span>
      <span className="tw:text-[10px] tw:font-semibold tw:leading-3">{label}</span>
    </Link>
  );
}

interface Props {
  pathname: string;
  /** Tap on Découvre — the layout turns a double-tap into "feed to the top". */
  onHomeTap: () => void;
}

export default function IosTabBar({ pathname, onHomeTap }: Props) {
  const { t } = useTranslation();

  return (
    <nav
      data-testid="ios-tab-bar"
      aria-label="Main navigation"
      className={cn(
        'tw:absolute tw:inset-x-4 tw:z-40 tw:flex tw:h-[62px] tw:items-stretch tw:gap-1 tw:rounded-full tw:p-1',
        // Over the home-indicator band like iOS 26 (≈ 20 px on Face ID phones), 12 px on an SE.
        'tw:bottom-[max(12px,calc(env(safe-area-inset-bottom)-14px))]',
        'tw:border tw:border-white/10 tw:shadow-lg',
        // Apple Music-like glass: a light tint over a strong blur (QA PO 2026-10-30).
        'tw:bg-dark-1/95 tw:supports-[backdrop-filter]:bg-dark-1/40 tw:backdrop-blur-2xl tw:backdrop-saturate-180',
      )}
    >
      <TabLink
        to={HOME_ROUTE}
        label={t('tabs.home')}
        badge
        icon={<HeadphonesFillIcon size={24} />}
        onClick={onHomeTap}
      />
      <TabLink to={MY_RECO_ROUTE} label={t('tabs.myReco')} icon={<RocketFillIcon size={24} />} />

      <RecoFab pathname={pathname} raised={false} />

      <TabLink
        to={COLLECTION_ROUTE}
        label={t('tabs.collection')}
        icon={<CardsStarFillIcon size={24} />}
      />
      <TabLink
        to={PROFILE_ROUTE}
        label={t('tabs.profile')}
        icon={<AccountCircleFillIcon size={24} />}
      />
    </nav>
  );
}
